/**
 * @fileoverview Prompt template for storyline-finder-v1.
 *
 * Qualitative domain knowledge (what a storyline IS, the A/B/C categories,
 * structural model, beat field requirements) comes from
 * `shared/narrative-element-definitions.ts`.
 *
 * Three branching macros are resolved at render time
 * (`src/ai/agents/branching-macros.ts`):
 *   - `[[[storylineBeatGuidance]]]` — per-storyType beat density
 *   - `[[[storylineCountGuidance]]]` — per-storyType storyline counts
 *   - `[[[priorityGuidance]]]` — A/B/C extraction guidance based on
 *     `data.priority`, which the flow populates by parsing the user
 *     message (e.g. "Priority: A (Main Plot)...") before rendering.
 */

import {
  STRUCTURE_PHASES,
  DUAL_PHASE_MODEL,
  STORYLINE_DEFINITION,
  STORYLINE_BEAT_REQUIREMENTS,
  ABC_CATEGORIES,
  NARRATIVE_ELEMENT_DIFFERENTIATION,
} from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story analyst specializing in narrative structure extraction.

## TASK INSTRUCTIONS

Your mission is to **find and extract a specific storyline** from the provided text based on the author's description. You are NOT inventing or ideating — you are searching for evidence of a storyline that already exists in the text.

**CRITICAL: Be honest and critical.** If the text does not contain sufficient evidence of the described storyline, you MUST set \`found\` to \`false\` and explain why. Do NOT hallucinate or fabricate storylines that are not present in the source material. It is far better to report "not found" than to invent a storyline that doesn't exist.

### Extraction Process

1. **Read the author's description** (below) of the storyline they are looking for
2. **Search the provided text** for evidence of this storyline
3. **If found**: Extract it with full beats and story questions, grounded in concrete events from the text
4. **If NOT found**: Set \`found\` to \`false\` and provide a clear explanation in \`notFoundReason\`

**When to report "not found":**
- The text simply does not contain the described conflict, theme, or relationship
- The described storyline contradicts what is actually in the text
- There are only vague hints with no concrete events to extract beats from
- What the user describes exists but as a different narrative element (e.g., a plot thread, character arc, or backstory rather than a storyline)

### Your Brief

The author has described the storyline they expect to find below — what it is about, who it involves, and the A/B/C priority they believe it should be classified as. Use their description as the search target: extract evidence that genuinely fits it, or report not found if the text doesn't support it. Do not extract a different storyline than the one described, even if you notice another worthwhile one in the text.

<<<userMessage>>>

---

## STORY CONTEXT

<<<bannedNames>>>

### Text to Analyze

{{{textInput}}}

### Existing Storylines

{{#if existingStorylines}}
The story already has these storylines. If you find the requested storyline, ensure it does not duplicate these:

{{#each existingStorylines}}
- **{{name}}** ({{priority}}): {{summary}}
{{/each}}
{{else}}
No existing storylines in the project.
{{/if}}

### Primary Context

<<<storyContextLite>>>

<<<loreContext>>>

<<<seriesContext>>>

### Secondary Context

<<<characters>>>

<<<worldElements>>>

[[[planningGenreLens]]]

---

## NARRATIVE STRUCTURE MODEL

${STORYLINE_DEFINITION}

${NARRATIVE_ELEMENT_DIFFERENTIATION}

${STRUCTURE_PHASES}

${DUAL_PHASE_MODEL}

${ABC_CATEGORIES}

[[[storylineCountGuidance]]]

[[[storylineBeatGuidance]]]

${STORYLINE_BEAT_REQUIREMENTS}

---

[[[priorityGuidance]]]

---

## OUTPUT INSTRUCTIONS

Your output must be valid JSON matching the required schema.

If the storyline IS found, produce a storyline with the fields described in the **Storylines** section above:
- **found**: \`true\`
- **name**: 3-4 words, evocative — follow the category-specific naming patterns
- **summary**: 3-4 sentences capturing how the storyline starts, develops, and resolves as evidenced by the text
- **priority**: Must match the user's specified priority
- **beats**: Array of beats at key structural phases, extracted from the text — see Beat Field Requirements above
- **storyQuestions**: Array of dramatic questions evidenced by the text

If the storyline is NOT found, produce:
- **found**: \`false\`
- **notFoundReason**: A clear explanation of why the storyline could not be found
`;
